import { useEffect, useMemo, useRef, useState } from 'react'
import { formatCountdown, resolveCountdownTarget } from '@/lib/limit-reached'

export type UsageLimitMode = 'rate_limit' | 'payment_required' | 'storage_exceeded'

export interface UsageLimitModalProps {
  open: boolean
  mode: UsageLimitMode
  resetsAt?: string | Date
  retryAfterSec?: number
  onUpgrade: () => void
  onDismiss: () => void
}

export function UsageLimitModal({
  open, mode, resetsAt, retryAfterSec, onUpgrade, onDismiss,
}: UsageLimitModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const target = useMemo(
    () => resolveCountdownTarget({ resetsAt, retryAfterSec }),
    [resetsAt, retryAfterSec],
  )
  const [now, setNow] = useState(() => Date.now())
  const dismissible = mode === 'rate_limit'

  useEffect(() => {
    const dlg = dialogRef.current
    if (!dlg) return
    // Native <dialog> is supported on every browser/WebView we target
    // (iOS Safari/WKWebView ≥ 15.4), so no polyfill is needed.
    if (open && !dlg.open) dlg.showModal()
    if (!open && dlg.open) dlg.close()
  }, [open])

  useEffect(() => {
    if (!open || mode !== 'rate_limit') return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [open, mode])

  useEffect(() => {
    if (!open) return
    const dlg = dialogRef.current
    // happy-dom does NOT dispatch `cancel` on Escape (its <dialog> is a stub);
    // listen for both `cancel` (real browsers) AND `keydown` (testable, works
    // in real browsers too). The keydown listener is bound to `document` so it
    // catches Escape regardless of focus target — important when happy-dom's
    // <dialog> stub leaves focus on <body>.
    const handleCancel = (e: Event) => {
      e.preventDefault()
      if (dismissible) onDismiss()
    }
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (dismissible) onDismiss()
      }
    }
    dlg?.addEventListener('cancel', handleCancel)
    document.addEventListener('keydown', handleKeydown)
    return () => {
      dlg?.removeEventListener('cancel', handleCancel)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [open, dismissible, onDismiss])

  if (!open) return null

  const countdown = mode === 'rate_limit' ? formatCountdown(target, now) : null

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="usage-limit-title"
      aria-describedby="usage-limit-body"
      className="rounded-xl p-0 max-w-md w-[calc(100%-32px)] border border-gray-200 shadow-2xl backdrop:bg-black/50"
    >
      <div className="p-6">
        <h2 id="usage-limit-title" tabIndex={-1} autoFocus className="text-lg font-semibold text-gray-900 outline-none">
          {mode === 'rate_limit'
            ? "You've reached your chat limit"
            : mode === 'storage_exceeded'
              ? 'Storage limit reached'
              : "You've reached your plan limit"}
        </h2>
        <p id="usage-limit-body" className="mt-2 text-sm text-gray-600">
          {mode === 'rate_limit'
            ? 'Upgrade to keep going, or wait for your limit to reset.'
            : mode === 'storage_exceeded'
              ? "You've used all your available storage. Upgrade to get more space."
              : 'Upgrade to keep using XAVIOR.'}
        </p>
        {countdown && (
          <p className="mt-4 text-sm text-gray-900">
            Resets in{' '}
            <span className="font-semibold tabular-nums" aria-live="off">
              {countdown}
            </span>
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          {dismissible && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Dismiss
            </button>
          )}
          <button
            type="button"
            onClick={onUpgrade}
            className="rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-600"
          >
            Upgrade
          </button>
        </div>
      </div>
    </dialog>
  )
}
